import { ExpandMore } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Typography } from "@mui/material";
import { forwardRef, memo, useCallback, useImperativeHandle, useRef } from "react";
import { FeedbackForm } from "src/components/gametable/FeedbackForm";
import EndgameNavigation from "src/components/gametable/redesign/EndgameNavigation";
import OverviewResultsTable from "src/components/gametable/redesign/OverviewResultsTable";
import ResultTable from "src/components/gametable/resultTable";
import { AllStats, PlayerColorInfo, TableInfo } from "src/components/types";

type ResultsFeedbackProps = {
  tableData: TableInfo | undefined;
  tableStatus: string;
  imagesLoaded: boolean;
  allStats: AllStats;
  playerColors?: PlayerColorInfo[];
  tableId?: string;
  playAsColor: string;
  scrollToFeedback: () => void;
  scrollToResults: () => void;
};

export interface ResultsFeedbackRefs {
  resultsRefEl: HTMLDivElement | null;
  feedbackRefEl: HTMLDivElement | null;
}

const ResultsFeedback = memo(
  forwardRef<ResultsFeedbackRefs, ResultsFeedbackProps>(function ResultsFeedback(
    {
      tableData,
      tableStatus,
      imagesLoaded,
      allStats,
      playerColors,
      playAsColor,
      tableId,
      scrollToFeedback,
      scrollToResults,
    }: ResultsFeedbackProps,
    ref,
  ) {
    const resultsRef = useRef<HTMLDivElement>(null);
    const feedbackRef = useRef<HTMLDivElement>(null);
    const overviewResultsRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => ({
      resultsRefEl: resultsRef.current,
      feedbackRefEl: feedbackRef.current,
    }));

    const scrollToOverviewResults = useCallback(() => {
      overviewResultsRef.current?.scrollIntoView({ behavior: "smooth" });
    }, []);

    const scrollToTable = useCallback(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, []);

    if (tableData && tableStatus == "ended" && imagesLoaded) {
      return (
        <div className="results-feedback-container">
          <div className="overview-results-container" ref={overviewResultsRef}>
            <Accordion defaultExpanded elevation={3}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">Score overview</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <OverviewResultsTable
                  allStats={allStats}
                  overviewStats={tableData?.projectInfo.overviewStats}
                  playerColors={playerColors ?? []}
                />
              </AccordionDetails>
            </Accordion>
          </div>
          <div className="feedback-container" ref={feedbackRef}>
            <Accordion defaultExpanded elevation={3}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">Feedback</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FeedbackForm
                  tableOptions={tableData.start_position.options}
                  questions={tableData.projectInfo.formQuestions}
                  answers={tableData.feedbackAnswers}
                  userAnswers={tableData.userAnswers}
                  tableId={tableId ?? ""}
                  color={playAsColor}
                />
              </AccordionDetails>
            </Accordion>
          </div>
          <div className="results-container" ref={resultsRef}>
            <Accordion defaultExpanded elevation={3}>
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="h6">Full statistics</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <ResultTable
                  allStats={allStats}
                  overviewStats={tableData?.projectInfo.overviewStats}
                  playerColors={playerColors ?? []}
                />
              </AccordionDetails>
            </Accordion>
          </div>
          <EndgameNavigation
            scrollToTable={scrollToTable}
            scrollToOverviewResults={scrollToOverviewResults}
            scrollToFeedback={scrollToFeedback}
            scrollToStatistics={scrollToResults}
          />
        </div>
      );
    }
    return <></>;
  }),
);

export default ResultsFeedback;
